// Google Analytics utility functions

/**
 * Initialize Google Analytics
 * Loads gtag.js dynamically and sets up tracking
 */
export const initGA = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    console.warn('Google Analytics Measurement ID not configured');
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false, // We'll send page views manually
  });
};

/**
 * Send page view to Google Analytics
 * @param {string} url - The page URL
 * @param {string} title - The page title
 */
export const logPageView = (url, title) => {
  if (window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title,
    });
  }
};

/**
 * Send custom event to Google Analytics
 * @param {string} action - Event action (e.g., 'click', 'play', 'purchase')
 * @param {string} category - Event category (e.g., 'button', 'video', 'form')
 * @param {string} label - Event label
 * @param {number} value - Event value (optional)
 */
export const logEvent = (action, category, label, value) => {
  if (window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

/**
 * Log premium purchase event
 * @param {string} planName - The purchased plan name
 * @param {number} price - The price
 * @param {string} currency - Currency code
 */
export const logPurchase = (planName, price, currency = 'MNT') => {
  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: Date.now().toString(),
      value: price,
      currency: currency,
      items: [
        {
          item_name: planName,
          price: price,
          quantity: 1,
        },
      ],
    });
  }
};

/**
 * Log user login
 * @param {string} method - Login method (e.g., 'phone', 'email')
 */
export const logLogin = (method) => {
  if (window.gtag) {
    window.gtag('event', 'login', {
      method: method,
    });
  }
};

/**
 * Log user signup
 * @param {string} method - Signup method
 */
export const logSignup = (method) => {
  if (window.gtag) {
    window.gtag('event', 'sign_up', {
      method: method,
    });
  }
};

/**
 * Log music play event
 * @param {string} songTitle - Song title
 * @param {string} artist - Artist name
 */
export const logMusicPlay = (songTitle, artist) => {
  if (window.gtag) {
    window.gtag('event', 'play_music', {
      event_category: 'Music',
      event_label: `${songTitle} - ${artist}`,
    });
  }
};

/**
 * Log search event
 * @param {string} searchTerm - Search query
 */
export const logSearch = (searchTerm) => {
  if (window.gtag) {
    window.gtag('event', 'search', {
      search_term: searchTerm,
    });
  }
};
